import { toast } from 'react-toastify';
import { CashierBadgeData } from '../../../../shared/user/cashier';
import { StringTranslator } from '../../../translations';
import {
  CardData,
  NFCTagInterfaceBase,
  NFCTagInterfaceType,
  parseNFCTagInterfaceUid,
  validateSignature,
} from '../NFCProxy';
import { PrinterPreferenceMode } from '../cashiercontext';
import { NFCTagHTTPProxyInterface } from '../nfcInterfaces/NFCTagHTTPProxyInterface';
import { NFCTagSerialInterface } from '../nfcInterfaces/NFCTagSerialInterface';
import { CashierAction } from '../reducer/reducer';
import { NFCSaveStatus } from './reducer';

export class NFCController {
  public NFCTagInterfaces: NFCTagInterfaceBase[] = [];

  public constructor(
    public dispatch: (action: CashierAction) => void,
    public ts: StringTranslator,
    saved?: NFCSaveStatus,
  ) {
    const newInterfaces =
      saved?.interfaces.flatMap((nfcTagInterface): NFCTagInterfaceBase[] => {
        const nfcTagInterfaceType = parseNFCTagInterfaceUid(nfcTagInterface.originalDeviceUID);
        if (!nfcTagInterfaceType) {
          return [];
        }

        switch (nfcTagInterfaceType.type) {
          case NFCTagInterfaceType.HTTP: {
            return [new NFCTagHTTPProxyInterface(nfcTagInterface.originalDeviceUID)];
          }

          case NFCTagInterfaceType.USBSerial: {
            return [new NFCTagSerialInterface(nfcTagInterface.originalDeviceUID)];
          }

          default: {
            return [];
          }
        }
      }) || [];

    const hasDefaultInterface = newInterfaces.some(
      (iface) => iface.originalDeviceUID === 'http:localhost:7070',
    );

    if (!hasDefaultInterface) {
      const defaultInterface = new NFCTagHTTPProxyInterface('http:localhost:7070');
      defaultInterface.httpAddress = 'http:localhost:7070';
      newInterfaces.push(defaultInterface);
    }

    this.NFCTagInterfaces = newInterfaces;

    void navigator.serial?.getPorts().then((newPorts) => {
      for (const port of newPorts) {
        const info = port.getInfo();
        const existingInterface = this.NFCTagInterfaces.find(
          (i) => i.info?.usbProductId === info.usbProductId,
        );

        if (existingInterface && !existingInterface.port) {
          setAndOpenSerialPort(port, existingInterface);
        }
      }
    });
  }

  public getSaveState(current?: string): NFCSaveStatus {
    return {
      current,
      interfaces: this.NFCTagInterfaces.map((iface) => ({
        originalDeviceUID: iface.originalDeviceUID,
      })),
    };
  }

  public dispose(): void {
    for (const iface of this.NFCTagInterfaces) {
      iface.dispose();
    }
  }

  public addInterface(tagInterface: NFCTagInterfaceBase): void {
    this.NFCTagInterfaces.push(tagInterface);
  }

  public removeInterface(tagInterface: NFCTagInterfaceBase): void {
    this.NFCTagInterfaces = this.NFCTagInterfaces.filter((iface) => iface !== tagInterface);
  }

  public async readNfcBadge(NfcTagInterface: NFCTagInterfaceBase): Promise<void> {
    // First get the UUID from the local service
    const uuidData = await NfcTagInterface.getUUID();

    if (!uuidData.success) {
      this.setNfcReaderStatus(this.ts('nfc_reader_failed_to_get_uuid'));
      return;
    }

    // Check if there is a password by trying to read with no password
    const { success, status } = await NfcTagInterface.checkCardBlank(uuidData.uuid);
    if (success) {
      this.setNfcReaderStatus(status);
      return;
    }

    // Then get the password for the NFC badge
    let response;
    try {
      response = await api.getNfcPassword(uuidData.uuid);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Request failed Unexpected response format')
      ) {
        this.setNfcReaderStatus(this.ts('nfc_card_present_uuid_not_found'));
        return;
      }

      throw error;
    }

    // Check if there is a password by trying to read with no password
    const cardData = await NfcTagInterface.readCard(uuidData.uuid, response.password);
    if (!cardData.success) {
      this.setNfcReaderStatus(
        this.ts('nfc_badge_problem', 'Cannot read this card (wrong password)'),
      );

      return;
    }

    if (!cardData.card) {
      this.setNfcReaderStatus(this.ts('nfc_badge_problem', 'No card data received'));
      return;
    }

    const isValid = await validateSignature({ card: cardData.card });

    if (!isValid) {
      this.setNfcReaderStatus(this.ts('nfc_card_signature_verification_failed'));
      this.setNfcReaderStatus(this.ts('nfc_badge_problem', 'Card has invalid signature'));
      return;
    }

    // Card data verified successfully

    // Check if we're already on the cashier page for an attendee
    const currentPath = window.location.pathname;
    if (currentPath !== '/housekeeping/cashier') {
      // Already on cashier page for an attendee, skipping badge read
      this.setNfcReaderStatus(this.ts('nfc_card_verified'));
      return;
    }

    // Reload the cashier interface with the attendeeId
    const { attendeeId } = cardData.card;

    window.location.href = `/housekeeping/cashier/${attendeeId}`;
  }

  public getNFCTagInterface(NFCTagInterfaceString: string): NFCTagInterfaceBase | undefined {
    return this.NFCTagInterfaces.find((t) => t.originalDeviceUID === NFCTagInterfaceString);
  }

  public async programNFCBadge(
    NfcTagInterface: NFCTagInterfaceBase,
    badge: CashierBadgeData,
    reason?: string,
    bypassWarning = false,
  ): Promise<boolean> {
    if (badge.nfc?.UUID && !bypassWarning) {
      // This handles the case where the user already has an NFC badge
      this.dispatch({
        type: 'NFC_SHOW_WARNING',
        source: 'nfc',
        badge,
        reason,
      });

      return false;
    }

    return await this.doProgramNfcBadge(NfcTagInterface, badge);
  }

  public async doProgramNfcBadge(
    NfcTagInterface: NFCTagInterfaceBase,
    badge: CashierBadgeData,
  ): Promise<boolean> {
    if (!badge.labels[0]) {
      return false;
    }

    // First get the UUID from the local service
    const uuidData = await NfcTagInterface.getUUID();

    if (!uuidData.success) {
      toast.error(this.ts('nfc_badge_problem', 'Failed to get UUID'));
      return false;
    }

    // Check there is no password by trying to read with no password
    const { success, status: cardStatus } = await NfcTagInterface.checkCardBlank(uuidData.uuid);
    if (!success) {
      toast.error(this.ts('nfc_badge_problem', cardStatus));
      return false;
    }

    // Then generate the NFC badge with the UUID
    const response = await api.generateNfcBadge(badge.labels[0].userId, uuidData.uuid);

    // Set the password for the NFC badge
    const passwordData = await NfcTagInterface.setPassword(uuidData.uuid, response.password);

    if (!passwordData.success) {
      await api.deleteNfcBadge(uuidData.uuid);
      toast.error(this.ts('nfc_badge_problem', 'Failed to set NFC badge password'));
      return false;
    }

    // Write the badge data
    const cardData: CardData = {
      attendeeId: badge.labels[0].userId,
      conventionId: response.signedContent.conventionId,
      issuance: response.signedContent.issuanceCount,
      timestamp: response.signedContent.timestamp,
      password: response.password,
      uuid: uuidData.uuid,
      signature: response.signature,
    };

    const writeData = await NfcTagInterface.writeCard(cardData);

    if (!writeData.success) {
      await api.deleteNfcBadge(uuidData.uuid);
      toast.error(this.ts('nfc_badge_problem', `Failed to write badge data: ${writeData.status}`));
      return false;
    }

    this.dispatch({ type: 'NFC_UPDATE_STATUS', source: 'nfc', status: 'Resetting...' });

    // Reset the badge since it's still unlocked after the write
    const resetResult = await NfcTagInterface.reset();
    if (resetResult.success) {
      // Forces the badge to re-read after we programmed it.
      this.readNfcBadge(NfcTagInterface).catch((error) => {
        if (error instanceof Error) {
          toast.error(error.message);
        }

        throw error;
      });
    } else {
      // Writing still worked - We won't abort at this point, just can't read the card
    }

    toast.success(this.ts('common.success'));
    return true;
  }

  public setActiveInterface(deviceUID: string): NFCTagInterfaceBase | undefined {
    const currentInterface = this.getNFCTagInterface(deviceUID);
    if (currentInterface) {
      currentInterface.startEventSource(
        (event: string) => {
          this.dispatch({ type: 'NFC_UPDATE_STATUS', source: 'nfc', status: event });
          if (event === 'Card present') {
            this.readNfcBadge(currentInterface).catch((error) => {
              if (error instanceof Error) {
                toast.error(error.message);
              }

              throw error;
            });
          }
        },
        (status: string) => {
          this.dispatch({ type: 'NFC_UPDATE_STATUS', source: 'nfc', status });
        },
        (status: string) => {
          this.dispatch({ type: 'NFC_UPDATE_STATUS', source: 'nfc', status });
        },
      );
    }

    return currentInterface;
  }

  public async handleNfcWarningYes(
    NfcTagInterface: NFCTagInterfaceBase,
    badge: CashierBadgeData,
    reason?: string,
  ): Promise<void> {
    const success = await this.programNFCBadge(NfcTagInterface, badge, undefined, true);
    if (success) {
      this.printBadge(badge, reason);
    }
  }

  public printBadge(badge: CashierBadgeData, reason?: string): void {
    this.dispatch({
      type: 'ZEBRA_PRINT',
      source: 'zebra',
      badge: badge.labels[0]!,
      design: badge.design,
      printer: PrinterPreferenceMode.Primary,
      reason,
    });
  }

  private setNfcReaderStatus(status: string): void {
    this.dispatch({
      type: 'NFC_UPDATE_STATUS',
      source: 'nfc',
      status,
    });
  }
}

export function setAndOpenSerialPort(
  port: SerialPort,
  existingInterface: NFCTagInterfaceBase,
): void {
  void port
    .open({
      baudRate: 115_200,
      dataBits: 8,
      stopBits: 1,
      parity: 'none',
      flowControl: 'none',
    })
    .then(() => {
      void port.setSignals({ dataTerminalReady: true, requestToSend: true }).then(() => {
        existingInterface.port = port;
        existingInterface.init();
      });
    });
}
